/**
 * O TURNO QUE ESTAVA A CORRER QUANDO A PÁGINA FOI SUSPENSA.
 *
 * ── POR QUE ISTO PRECISA DE SAIR DA MEMÓRIA ───────────────────────────────
 *
 * O Safari do iOS suspende o JavaScript de uma página em segundo plano e, sob
 * pressão de memória, DESCARTA-A: ao voltar, a aba recarrega do zero. A pergunta
 * que estava em voo, a sessão em que ela ficou gravada e o instante do envio
 * viviam todos em variáveis de um `composable` que já não existe — e sem eles não
 * há o que consultar. A tela recarregada não sabe sequer que houve um turno.
 *
 * Guardado aqui, o regresso tem uma âncora: reata o fio se o turno ainda corre,
 * ou relê a sessão à procura da resposta que foi gravada enquanto ninguém via.
 *
 * ── POR QUE `sessionStorage` E NÃO `localStorage` ─────────────────────────
 *
 * Um turno pertence à ABA que o pediu. `sessionStorage` sobrevive ao recarregar e
 * à restauração de aba do Safari — que é exactamente a travessia que interessa —
 * e morre com a aba, que é o que impede uma segunda janela de tentar recuperar um
 * turno que ela nunca enviou e de desenhar a resposta na conversa errada.
 */

export interface PendingTurn {
	/** O agente, que é como o servidor nomeia um turno em voo (`turnKey`). */
	agent: string;
	/** Onde a resposta será arquivada. Pode chegar depois, no quadro `session`. */
	sessionId: string | null;
	/** A pergunta — a âncora que acha a resposta na thread. */
	prompt: string;
	/** Quando foi enviada, para um registo esquecido não ressuscitar amanhã. */
	at: number;
}

const KEY = "benvox.pendingTurn";
function keyFor(agent: string, sessionId?: string | null): string {
	return sessionId === undefined ? KEY : `${KEY}:${agent}:${sessionId ?? "new"}`;
}

/**
 * Depois disto o registo não vale nada.
 *
 * O turno mais longo já medido tem 675 s; meia hora é folga de sobra para o mais
 * lento que se consegue imaginar, e curta o bastante para uma aba restaurada no
 * dia seguinte não ir buscar a resposta de ontem para a pôr no ecrã de hoje.
 */
export const PENDING_STALE_MS = 30 * 60_000;

/**
 * O armazém, quando existe.
 *
 * Em navegação privada e com cookies de terceiros bloqueados, apenas TOCAR em
 * `sessionStorage` atira. Perder a recuperação é mau; derrubar o envio de um
 * turno por causa dela seria pior.
 */
function store(): Storage | null {
	try {
		return globalThis.sessionStorage ?? null;
	} catch {
		return null;
	}
}

/** Guarda o turno que acabou de sair. Substitui o anterior deste agente. */
export function rememberPendingTurn(turn: Omit<PendingTurn, "at">): void {
	try {
		const serialized = JSON.stringify({ ...turn, at: Date.now() });
		store()?.setItem(keyFor(turn.agent, turn.sessionId), serialized);
		store()?.setItem(KEY, serialized);
	} catch {
		// Quota, modo privado: a recuperação em memória continua a funcionar.
	}
}

/**
 * O turno pendente deste agente, se houver um e ainda valer.
 *
 * Pelo AGENTE porque é essa a chave do turno no servidor: o registo do construtor
 * não pode mandar a tela da Lúcia procurar uma resposta que não é dela.
 */
export function readPendingTurn(agent: string, sessionId?: string | null): PendingTurn | null {
	let raw: string | null = null;
	try {
		raw = store()?.getItem(keyFor(agent, sessionId)) ?? null;
	} catch {
		return null;
	}
	if (!raw) return null;
	let turn: PendingTurn;
	try {
		turn = JSON.parse(raw) as PendingTurn;
	} catch {
		return null;
	}
	if (typeof turn?.prompt !== "string" || turn.agent !== agent) return null;
	if (typeof turn.at !== "number" || Date.now() - turn.at > PENDING_STALE_MS) {
		forgetPendingTurn(agent, sessionId);
		return null;
	}
	return turn;
}

/** A resposta chegou, ou a busca desistiu: não há mais nada por que voltar. */
export function forgetPendingTurn(agent: string, sessionId?: string | null): void {
	const pending = (() => {
		try {
			return store()?.getItem(keyFor(agent, sessionId)) ?? null;
		} catch {
			return null;
		}
	})();
	// Só se apaga o próprio: entre ler e apagar pode ter saído um turno de OUTRO
	// agente, e limpar às cegas deixaria esse sem âncora nenhuma.
	if (pending) {
		try {
			const turn = JSON.parse(pending) as PendingTurn;
			if (turn?.agent !== agent) return;
		} catch {
			// Ilegível é lixo: apagar é o certo.
		}
	}
	try {
		store()?.removeItem(keyFor(agent, sessionId));
	} catch {
		// Nada a fazer, e nada que valha derrubar a tela.
	}
}
